import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { EmailInlineEditor } from '@app/components/EmailDesignEditor';
import { MessageInlineEditor } from '@app/components/MessageDesignEditor';
import { getBusinessInfo } from '@app/services/business';
import { getEmailPreviewHtml, getMessagePreviewHtml } from '@app/utils/htmlTemplates';
import { MessageTemplateChannel } from '@app/utils/consts';
import { BusinessInterface, EmailDesignContent, MessageDesignContent } from '@app/models';

type TemplateDesignEditorProps = { className?: string } & (
  | { channel: MessageTemplateChannel.EMAIL; value: EmailDesignContent; onChange: (value: EmailDesignContent) => void }
  | { channel: MessageTemplateChannel.SMS; value: MessageDesignContent; onChange: (value: MessageDesignContent) => void }
);

// Controlled email/SMS design editor with a live preview. Unlike TemplateList it keeps no state of its own and doesn't touch localStorage
export const TemplateDesignEditor = (props: TemplateDesignEditorProps) => {
  const { data: business } = useQuery({
    queryKey: ['business-info'],
    queryFn: () => getBusinessInfo(),
    select: (response) => response.data as BusinessInterface,
  });

  const businessLogo = business?.photo?.url ?? '';

  // The business logo is only a preview fallback, it isn't written into the value
  const previewHtml = useMemo(() => {
    if (props.channel === MessageTemplateChannel.EMAIL) {
      return getEmailPreviewHtml({ ...props.value, logoUrl: props.value.logoUrl || businessLogo });
    }

    return getMessagePreviewHtml({ ...props.value, logoUrl: props.value.logoUrl || businessLogo }, business?.name ?? '');
  }, [props.channel, props.value, businessLogo, business?.name]);

  return (
    <div className={clsx('flex gap-4 min-h-0', props.className)}>
      <div className="w-2/5 bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto p-6">
        <h3 className="text-sm font-bold text-gray-900">Design settings</h3>
        <div className="border-b border-gray-200 mb-6 mt-4"></div>

        {props.channel === MessageTemplateChannel.EMAIL ? (
          <EmailInlineEditor content={props.value} onChange={props.onChange} />
        ) : (
          <MessageInlineEditor content={props.value} onChange={props.onChange} />
        )}
      </div>

      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-gray-50 h-full p-6 overflow-hidden">
          <iframe srcDoc={previewHtml} title="Template preview" className="w-full h-full border-0 bg-white" />
        </div>
      </div>
    </div>
  );
};
